// src/pages/Patreon.js

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  SimpleGrid,
  Image,
  Icon,
  useColorModeValue,
  Spinner,
  Skeleton,
  SkeletonText,
  useToast,
  VStack,
  Container,
  Badge,
} from '@chakra-ui/react';

import {
  FaUsers,
  FaGift,
  FaStore,
  FaPalette,
  FaUtensils,
  FaGamepad,
  FaCalculator,
} from 'react-icons/fa';

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  createSubscription,
  fetchSubscriptions,
} from '../components/Subscriptions';
import SubscriptionDrawer from '../components/Subscription/SubscriptionDrawer';
import HowItWorksModal from '../components/Subscription/HowItWorksModal';
import VantaggiEsclusiviModal from '../components/Subscription/VantaggiEsclusiviModal';

// CIL Colors
const cilColors = {
  yellow: '#ffcc00',
  teal: '#3eb297',
  sky: '#36a9e1',
  pink: '#e95995',
  plum: '#3b235a',
  red: '#e73341',
};

// Helper per ottenere l'icona in base al tipo di sconto
const getTypeIcon = (type) => {
  const typeIcons = {
    ristorante: FaUtensils,
    negozio: FaStore,
    attivita: FaGamepad,
    cultura: FaPalette,
    servizi: FaGift,
    default: FaGift,
  };
  return typeIcons[type?.toLowerCase()] || typeIcons.default;
};

// Helper per ottenere il colore del badge in base al tipo
const getTypeColor = (type) => {
  const typeColors = {
    ristorante: 'orange',
    negozio: 'blue',
    attivita: 'green',
    cultura: 'purple',
    servizi: 'teal',
    default: 'gray',
  };
  return typeColors[type?.toLowerCase()] || typeColors.default;
};

const API_URL = process.env.REACT_APP_STRAPI_URL || '';
const withBase = (url) =>
  url?.startsWith('http') ? url : API_URL ? `${API_URL}${url || ''}` : url;
const track = (event, payload = {}) => {
  try {
    console.debug('[track]', event, payload);
  } catch (e) {
    // Silently handle tracking errors
  }
};

const Patreon = () => {
  const { userData } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [tiers, setTiers] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(true);
  const [loadingUserSubs, setLoadingUserSubs] = useState(true);
  const [selectedTier, setSelectedTier] = useState(null);
  const [subClientSecret, setSubClientSecret] = useState('');
  const [subLocalSubscriptionId, setSubLocalSubscriptionId] = useState('');

  // Nuovo state per il conteggio delle subscription attive
  const [activeSubscriptionsCount, setActiveSubscriptionsCount] = useState(0);

  // State per il modale "Come funziona"
  const [isHowItWorksOpen, setIsHowItWorksOpen] = useState(false);

  // State per il modale dei vantaggi esclusivi
  const [selectedSconto, setSelectedSconto] = useState(null);
  const [isVantaggiModalOpen, setIsVantaggiModalOpen] = useState(false);

  // ── Load subscription tiers ─────────────────────────────────────────
  useEffect(() => {
    const fetchTiers = async () => {
      try {
        const response = await fetch('/api/subscription-tiers');
        if (response.ok) {
          const data = await response.json();
          const tiersArray = Array.isArray(data)
            ? data
            : data?.data && Array.isArray(data.data)
              ? data.data
              : [];
          setTiers(tiersArray);
        }
      } catch (error) {
        console.error('Errore nel caricamento dei tier:', error);
        setTiers([]);
      } finally {
        setLoadingTiers(false);
      }
    };

    fetchTiers();
  }, []);

  // ── Load user subscriptions ────────────────────────────────────────
  useEffect(() => {
    const fetchUserSubs = async () => {
      if (!userData?.id) {
        setLoadingUserSubs(false);
        return;
      }

      try {
        // Fetch active subscriptions count
        const subsResponse = await fetchSubscriptions();
        if (subsResponse.data) {
          const activeSubs = subsResponse.data.filter(
            (sub) =>
              sub.status === 'active' || sub.subscriptionStatus === 'active'
          );
          setActiveSubscriptionsCount(activeSubs.length);
        }
      } catch (error) {
        console.error('Errore nel caricamento delle subscription:', error);
      } finally {
        setLoadingUserSubs(false);
      }
    };

    fetchUserSubs();
  }, [userData]);

  // ── Handle subscription ─────────────────────────────────────────────
  const handleSubscribe = async (tier) => {
    setSelectedTier(tier);
    try {
      const { data } = await createSubscription({
        userId: userData.id,
        tierId: tier.documentId,
        stripeCustomerId: userData.stripeCustomerId,
        stripePriceId: tier.stripePriceId,
        metadata: {},
      });
      setSubClientSecret(data.clientSecret);
      setSubLocalSubscriptionId(data.localSubscriptionId);
    } catch (err) {
      console.error('Errore creazione subscription', err);
      toast({ title: 'Errore durante la sottoscrizione', status: 'error' });
    }
  };

  // ── Open vantaggi esclusivi modal ──────────────────────────────────
  const openVantaggiModal = (sconto) => {
    setSelectedSconto(sconto);
    setIsVantaggiModalOpen(true);
    track('cta_vantaggi_esclusivi', {
      scontoId: sconto.documentId,
      scontoType: sconto.type,
    });
  };

  // ── Memoized calculations ──────────────────────────────────────────
  const memoizedTiers = useMemo(() => tiers, [tiers]);

  if (loadingUserSubs) {
    return (
      <Flex justify="center" py={20}>
        <Spinner size="xl" />
      </Flex>
    );
  }

  // Se l'utente non è loggato, mostra solo la hero con bottone "Diventa Socio"
  if (!userData) {
    return (
      <Box>
        {/* HERO SECTION - Solo per utenti non loggati */}
        <Box
          position="relative"
          w="full"
          h="70vh"
          minH="500px"
          overflow="hidden"
          mb={0}
        >
          <Image
            src={
              withBase(
                'https://cms.cilassociazione.it/uploads/carnevale_munlab_d7caee7803.jpg'
              ) || '/fallback-hero.jpg'
            }
            alt="Abbonamenti CIL - sostegno mensile"
            w="full"
            h="full"
            objectFit="cover"
          />

          {/* Overlay scuro per migliorare la leggibilità */}
          <Box
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="blackAlpha.700"
          />

          {/* Contenuto centrato con messaggio di tesseramento */}
          <Flex
            position="absolute"
            top={0}
            left={0}
            right={0}
            bottom={0}
            align="center"
            justify="center"
            direction="column"
            textAlign="center"
            px={6}
          >
            <Heading
              size="4xl"
              color="white"
              mb={6}
              textShadow="2px 2px 4px rgba(0,0,0,0.8)"
              fontWeight="extrabold"
              letterSpacing="wide"
              maxW="800px"
            >
              Abbonamenti CIL
            </Heading>

            <Text
              fontSize="xl"
              color="white"
              maxW="600px"
              mb={8}
              textShadow="1px 1px 2px rgba(0,0,0,0.8)"
              fontWeight="medium"
              lineHeight="1.7"
            >
              Per abbonarti e accedere ai vantaggi esclusivi, devi prima
              diventare socio dell&apos;associazione.
            </Text>

            {/* CTA per tesseramento */}
            <Button
              size="xl"
              bg={cilColors.yellow}
              color={cilColors.plum}
              _hover={{ bg: '#e6b800', transform: 'scale(1.05)' }}
              px={12}
              py={6}
              fontWeight="bold"
              fontSize="lg"
              leftIcon={<FaUsers />}
              transition="all 0.3s"
              borderRadius="md"
              onClick={() => navigate('/login')}
            >
              Entra in CIL
            </Button>
          </Flex>
        </Box>
      </Box>
    );
  }

  return (
    <Box>
      {/* HERO SECTION */}
      <Box
        position="relative"
        w="full"
        h="70vh"
        minH="500px"
        overflow="hidden"
        mb={0}
      >
        <Image
          src={
            withBase(
              'https://cms.cilassociazione.it/uploads/carnevale_munlab_d7caee7803.jpg'
            ) || '/fallback-hero.jpg'
          }
          alt="Abbonamenti CIL - sostegno mensile"
          w="full"
          h="full"
          objectFit="cover"
        />

        {/* Overlay scuro per migliorare la leggibilità */}
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="blackAlpha.700"
        />

        {/* Contenuto centrato */}
        <Flex
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          align="center"
          justify="center"
          direction="column"
          textAlign="center"
          px={6}
        >
          <Heading
            size="4xl"
            color="white"
            mb={6}
            textShadow="2px 2px 4px rgba(0,0,0,0.8)"
            fontWeight="extrabold"
            letterSpacing="wide"
            maxW="800px"
          >
            Diventa Sostenitore Mensile
          </Heading>

          <Text
            fontSize="xl"
            color="white"
            maxW="600px"
            mb={8}
            textShadow="1px 1px 2px rgba(0,0,0,0.8)"
            fontWeight="medium"
            lineHeight="1.7"
          >
            Scegli il piano che fa per te e sostieni CIL ogni mese con benefici
            esclusivi in arrivo!
          </Text>
        </Flex>
      </Box>

      {/* SEZIONE SPIEGAZIONE DONAZIONI RICORRENTI */}
      <Box py={16} bg="gray.50">
        <Container maxW="1000px">
          <VStack spacing={12}>
            {/* Titolo principale */}
            <VStack spacing={6} textAlign="center">
              <Box bg={cilColors.teal} borderRadius="lg" p={4} boxShadow="lg">
                <Icon as={FaGift} w={8} h={8} color="white" />
              </Box>
              <Heading size="2xl" color={cilColors.plum} fontWeight="bold">
                Cosa sono le Donazioni Ricorrenti?
              </Heading>
              <Text
                fontSize="lg"
                color="gray.600"
                maxW="800px"
                lineHeight="1.7"
              >
                Le donazioni ricorrenti sono il cuore pulsante di CIL. Come una
                startup che cresce passo dopo passo, abbiamo bisogno di un
                flusso costante di risorse per realizzare ogni nostra idea e
                sostenere i valori fondamentali del nostro statuto.
              </Text>
            </VStack>

            {/* Griglia con i benefici */}
            <SimpleGrid columns={[1, 2, 3]} spacing={8} w="full">
              {/* Stabilità finanziaria */}
              <Box
                bg="white"
                p={6}
                borderRadius="md"
                boxShadow="lg"
                textAlign="center"
                border="2px solid"
                borderColor="transparent"
                _hover={{
                  borderColor: cilColors.teal,
                  transform: 'translateY(-4px)',
                }}
                transition="all 0.3s"
              >
                <Box
                  bg={`${cilColors.teal}20`}
                  borderRadius="md"
                  p={4}
                  w="fit-content"
                  mx="auto"
                  mb={4}
                >
                  <Icon as={FaUsers} w={8} h={8} color={cilColors.teal} />
                </Box>
                <Heading size="md" color={cilColors.plum} mb={4}>
                  Stabilità Finanziaria
                </Heading>
                <Text color="gray.600" lineHeight="1.6">
                  Un flusso costante di entrate ci permette di pianificare
                  progetti a lungo termine, assumere personale qualificato e
                  investire in infrastrutture durature.
                </Text>
              </Box>

              {/* Crescita sostenibile */}
              <Box
                bg="white"
                p={6}
                borderRadius="md"
                boxShadow="lg"
                textAlign="center"
                border="2px solid"
                borderColor="transparent"
                _hover={{
                  borderColor: cilColors.sky,
                  transform: 'translateY(-4px)',
                }}
                transition="all 0.3s"
              >
                <Box
                  bg={`${cilColors.sky}20`}
                  borderRadius="md"
                  p={4}
                  w="fit-content"
                  mx="auto"
                  mb={4}
                >
                  <Icon as={FaGamepad} w={8} h={8} color={cilColors.sky} />
                </Box>
                <Heading size="md" color={cilColors.plum} mb={4}>
                  Crescita Sostenibile
                </Heading>
                <Text color="gray.600" lineHeight="1.6">
                  Come una startup, CIL cresce attraverso innovazione e
                  sperimentazione. Le donazioni ricorrenti sono il nostro
                  &ldquo;boost&rdquo; per trasformare ogni idea in realtà.
                </Text>
              </Box>

              {/* Valori dello statuto */}
              <Box
                bg="white"
                p={6}
                borderRadius="md"
                boxShadow="lg"
                textAlign="center"
                border="2px solid"
                borderColor="transparent"
                _hover={{
                  borderColor: cilColors.pink,
                  transform: 'translateY(-4px)',
                }}
                transition="all 0.3s"
              >
                <Box
                  bg={`${cilColors.pink}20`}
                  borderRadius="md"
                  p={4}
                  w="fit-content"
                  mx="auto"
                  mb={4}
                >
                  <Icon as={FaPalette} w={8} h={8} color={cilColors.pink} />
                </Box>
                <Heading size="md" color={cilColors.plum} mb={4}>
                  Valori Fondamentali
                </Heading>
                <Text color="gray.600" lineHeight="1.6">
                  Ogni donazione sostiene direttamente i principi del nostro
                  statuto: inclusione, cultura, innovazione sociale e sviluppo
                  del territorio.
                </Text>
              </Box>
            </SimpleGrid>

            {/* Call to action centrale */}
            <Box
              bg={`linear-gradient(135deg, ${cilColors.teal} 0%, ${cilColors.sky} 100%)`}
              borderRadius="md"
              p={8}
              textAlign="center"
              color="white"
              position="relative"
              overflow="hidden"
            >
              {/* Elementi decorativi */}
              <Box
                position="absolute"
                top="-50px"
                right="-50px"
                w="100px"
                h="100px"
                borderRadius="full"
                bg="rgba(255, 255, 255, 0.1)"
                filter="blur(20px)"
              />
              <Box
                position="absolute"
                bottom="-30px"
                left="-30px"
                w="60px"
                h="60px"
                borderRadius="full"
                bg="rgba(255, 255, 255, 0.1)"
                filter="blur(15px)"
              />

              <VStack spacing={6} position="relative" zIndex={2}>
                <Heading size="xl" fontWeight="bold">
                  Il Tuo Contributo Fa la Differenza
                </Heading>
                <Text
                  fontSize="lg"
                  maxW="600px"
                  lineHeight="1.7"
                  opacity={0.95}
                >
                  Con una donazione ricorrente mensile, diventi parte attiva
                  della crescita di CIL. Non stai solo sostenendo
                  un&apos;associazione, stai investendo nel futuro della
                  comunità e nella realizzazione di progetti che cambiano il
                  territorio.
                </Text>
                <Button
                  size="lg"
                  bg={cilColors.yellow}
                  color={cilColors.plum}
                  _hover={{ bg: '#e6b800', transform: 'scale(1.05)' }}
                  px={8}
                  py={6}
                  fontWeight="bold"
                  fontSize="md"
                  transition="all 0.3s"
                  borderRadius="md"
                  onClick={() => {
                    document
                      .getElementById('subscription-plans')
                      ?.scrollIntoView({ behavior: 'smooth' });
                    track('cta_scopri_piani');
                  }}
                >
                  Scopri i Piani di Sostegno
                </Button>
              </VStack>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* SUBSCRIPTION TIERS */}
      <Box
        id="subscription-plans"
        py={24}
        position="relative"
        overflow="hidden"
        bg="linear-gradient(135deg, #3b235a 0%, #3eb297 50%, #36a9e1 100%)"
        _before={{
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: `
            radial-gradient(circle at 20% 80%, rgba(255, 204, 0, 0.1) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(62, 178, 151, 0.15) 0%, transparent 50%),
            radial-gradient(circle at 40% 40%, rgba(54, 169, 225, 0.1) 0%, transparent 50%)
          `,
          zIndex: 1,
        }}
      >
        <Container maxW="1200px" position="relative" zIndex={2}>
          <VStack spacing={8}>
            <VStack spacing={6} textAlign="center">
              <Box
                bg="rgba(255, 255, 255, 0.2)"
                borderRadius="lg"
                p={6}
                backdropFilter="blur(15px)"
                border="2px solid rgba(255, 255, 255, 0.3)"
                position="relative"
                className="group"
                boxShadow="0 8px 32px rgba(0, 0, 0, 0.1)"
              >
                {/* Badge contatore migliorato */}
                {activeSubscriptionsCount > 0 && (
                  <Box
                    position="absolute"
                    top="-12px"
                    right="-12px"
                    bg={cilColors.yellow}
                    borderRadius="md"
                    w="40px"
                    h="40px"
                    display="flex"
                    alignItems="center"
                    justifyContent="center"
                    boxShadow="0 4px 20px rgba(255, 204, 0, 0.4)"
                    zIndex={2}
                    cursor="pointer"
                    _groupHover={{
                      transform: 'scale(1.15)',
                      transition: 'transform 0.3s ease-in-out',
                    }}
                  >
                    <Text
                      fontSize="md"
                      fontWeight="bold"
                      color={cilColors.plum}
                    >
                      {activeSubscriptionsCount}
                    </Text>

                    {/* Tooltip migliorato */}
                    <Box
                      position="absolute"
                      top="50px"
                      right="-30px"
                      bg="white"
                      borderRadius="md"
                      p={4}
                      boxShadow="0 10px 40px rgba(0, 0, 0, 0.15)"
                      border="1px solid rgba(255, 255, 255, 0.2)"
                      opacity={0}
                      visibility="hidden"
                      transform="translateY(-10px)"
                      transition="all 0.3s ease-in-out"
                      _groupHover={{
                        opacity: 1,
                        visibility: 'visible',
                        transform: 'translateY(0)',
                      }}
                      zIndex={3}
                      minW="220px"
                      textAlign="center"
                    >
                      <Text
                        fontSize="sm"
                        fontWeight="semibold"
                        color="gray.700"
                        lineHeight="1.4"
                      >
                        Unisciti a{' '}
                        <Text
                          as="span"
                          color={cilColors.yellow}
                          fontWeight="bold"
                        >
                          {activeSubscriptionsCount}
                        </Text>{' '}
                        Sostenitori Attivi
                      </Text>

                      {/* Arrow migliorato */}
                      <Box
                        position="absolute"
                        top="-8px"
                        right="35px"
                        w="0"
                        h="0"
                        borderLeft="8px solid transparent"
                        borderRight="8px solid transparent"
                        borderBottom="8px solid white"
                      />
                    </Box>
                  </Box>
                )}
                <Icon as={FaUsers} w={12} h={12} color="white" />
              </Box>

              <Heading
                textAlign="center"
                color="white"
                size="3xl"
                textShadow="2px 2px 8px rgba(0,0,0,0.3)"
                fontWeight="extrabold"
                letterSpacing="wide"
              >
                Diventa Sostenitore Mensile
              </Heading>

              <Text
                textAlign="center"
                fontSize="xl"
                color="white"
                maxW="800px"
                textShadow="1px 1px 4px rgba(0,0,0,0.3)"
                lineHeight="1.6"
                fontWeight="medium"
              >
                Scegli il piano che fa per te e sostieni CIL ogni mese con
                benefici esclusivi in arrivo!
              </Text>

              {/* Badge temporale */}
              <Box
                bg="rgba(255, 255, 255, 0.15)"
                borderRadius="md"
                px={6}
                py={2}
                backdropFilter="blur(10px)"
                border="1px solid rgba(255, 255, 255, 0.2)"
              >
                <Text fontSize="sm" color="white" fontWeight="semibold">
                  🎁 Benefici esclusivi in arrivo!
                </Text>
              </Box>
            </VStack>

            {loadingTiers ? (
              <SimpleGrid columns={[1, 2, 3]} spacing={6} w="full">
                {[1, 2, 3].map((i) => (
                  <Box
                    key={`skeleton-tier-${i}`}
                    bg="white"
                    borderRadius="md"
                    overflow="hidden"
                    boxShadow="md"
                    minH="320px"
                  >
                    <Skeleton height="120px" />
                    <Box p={6}>
                      <Skeleton height="32px" mb={4} />
                      <SkeletonText noOfLines={4} mb={4} />
                      <Skeleton height="40px" />
                    </Box>
                  </Box>
                ))}
              </SimpleGrid>
            ) : !Array.isArray(memoizedTiers) || memoizedTiers.length === 0 ? (
              <Box textAlign="center" py={12}>
                <Text fontSize="lg" color="gray.500">
                  Nessun piano attivo al momento
                </Text>
              </Box>
            ) : (
              <SimpleGrid columns={[1, 2, 3]} spacing={6} w="full">
                {memoizedTiers
                  .sort(
                    (a, b) => (Number(a.price) || 0) - (Number(b.price) || 0)
                  )
                  .map((tier) => {
                    const cardBg = useColorModeValue('white', 'gray.800');
                    return (
                      <Box
                        key={tier.documentId}
                        bg={cardBg}
                        borderRadius="md"
                        overflow="hidden"
                        boxShadow="0 20px 40px rgba(0, 0, 0, 0.1)"
                        cursor="pointer"
                        transition="all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                        _hover={{
                          transform: 'translateY(-12px) scale(1.02)',
                          boxShadow: '0 30px 60px rgba(0, 0, 0, 0.15)',
                          borderColor: cilColors.teal,
                        }}
                        minH="400px"
                        display="flex"
                        flexDirection="column"
                        border="3px solid"
                        borderColor="transparent"
                      >
                        {/* Header migliorato con design più moderno */}
                        <Box
                          position="relative"
                          p={8}
                          textAlign="center"
                          minH="140px"
                          display="flex"
                          flexDirection="column"
                          justifyContent="center"
                          overflow="hidden"
                          bg={`
                          linear-gradient(135deg, ${cilColors.teal} 0%, ${cilColors.sky} 100%)
                        `}
                        >
                          {/* Pattern decorativo */}
                          <Box
                            position="absolute"
                            top={0}
                            left={0}
                            right={0}
                            bottom={0}
                            opacity="0.1"
                            backgroundImage="radial-gradient(circle at 20% 20%, white 2px, transparent 2px)"
                            backgroundSize="20px 20px"
                          />

                          {/* Thumbnail di sfondo opzionale */}
                          {tier.thumbnail && (
                            <Image
                              src={withBase(
                                tier.thumbnail.formats?.medium?.url ||
                                  tier.thumbnail.url
                              )}
                              alt={`${tier.name} thumbnail`}
                              position="absolute"
                              top={0}
                              left={0}
                              right={0}
                              bottom={0}
                              w="full"
                              h="full"
                              objectFit="cover"
                              filter="brightness(0.3)"
                              opacity="0.3"
                            />
                          )}

                          {/* Contenuto testuale migliorato */}
                          <Box position="relative" zIndex={2}>
                            <Heading
                              size="xl"
                              color="white"
                              mb={3}
                              fontWeight="extrabold"
                              textShadow="2px 2px 4px rgba(0,0,0,0.3)"
                            >
                              {tier.name}
                            </Heading>

                            <Box mb={2}>
                              <Text
                                fontSize="4xl"
                                fontWeight="black"
                                color="white"
                                textShadow="2px 2px 4px rgba(0,0,0,0.3)"
                                lineHeight="1"
                              >
                                €{tier.price}
                              </Text>
                              <Text
                                fontSize="sm"
                                color="whiteAlpha.900"
                                fontWeight="medium"
                                textShadow="1px 1px 2px rgba(0,0,0,0.3)"
                              >
                                al mese
                              </Text>
                            </Box>
                          </Box>
                        </Box>

                        {/* Corpo della card migliorato */}
                        <Box
                          p={8}
                          flex="1"
                          display="flex"
                          flexDirection="column"
                        >
                          <Box
                            mb={4}
                            lineHeight="1.6"
                            color="gray.600"
                            textAlign="center"
                          >
                            {/* Gestione Rich Text Blocks di Strapi */}
                            {(() => {
                              // Se non c'è descrizione
                              if (!tier.description) {
                                return (
                                  <Text
                                    color="gray.500"
                                    fontStyle="italic"
                                    textAlign="center"
                                  >
                                    Descrizione non disponibile
                                  </Text>
                                );
                              }

                              // Se è un array (rich text blocks)
                              if (Array.isArray(tier.description)) {
                                return (
                                  <Box>
                                    {tier.description.map((block) => {
                                      const { type, children, format } = block;
                                      const blockId = `${tier.documentId}-${type}-${Math.random().toString(36).substr(2, 9)}`;

                                      // Gestione dei diversi tipi di blocchi
                                      switch (type) {
                                        case 'paragraph':
                                          return (
                                            <Text
                                              key={blockId}
                                              mb={2}
                                              lineHeight="1.6"
                                              textAlign="center"
                                            >
                                              {children?.map((child) => {
                                                const {
                                                  type: childType,
                                                  text,
                                                  bold,
                                                  italic,
                                                  underline,
                                                } = child;
                                                const childId = `${blockId}-${childType}-${Math.random().toString(36).substr(2, 9)}`;

                                                if (childType === 'text') {
                                                  let formattedText = text;
                                                  if (bold)
                                                    formattedText = (
                                                      <Text
                                                        as="span"
                                                        key={childId}
                                                        fontWeight="bold"
                                                        color={cilColors.teal}
                                                      >
                                                        {text}
                                                      </Text>
                                                    );
                                                  if (italic)
                                                    formattedText = (
                                                      <Text
                                                        as="span"
                                                        key={childId}
                                                        fontStyle="italic"
                                                      >
                                                        {text}
                                                      </Text>
                                                    );
                                                  if (underline)
                                                    formattedText = (
                                                      <Text
                                                        as="span"
                                                        key={childId}
                                                        textDecoration="underline"
                                                      >
                                                        {text}
                                                      </Text>
                                                    );
                                                  return formattedText;
                                                }
                                                return null;
                                              })}
                                            </Text>
                                          );

                                        case 'heading':
                                          return (
                                            <Heading
                                              key={blockId}
                                              size="sm"
                                              mb={2}
                                              mt={3}
                                              color={cilColors.plum}
                                            >
                                              {children?.map((child) => {
                                                const {
                                                  type: childType,
                                                  text,
                                                  bold,
                                                  italic,
                                                } = child;
                                                const childId = `${blockId}-${childType}-${Math.random().toString(36).substr(2, 9)}`;

                                                if (childType === 'text') {
                                                  let formattedText = text;
                                                  if (bold)
                                                    formattedText = (
                                                      <Text
                                                        as="span"
                                                        key={childId}
                                                        fontWeight="bold"
                                                      >
                                                        {text}
                                                      </Text>
                                                    );
                                                  if (italic)
                                                    formattedText = (
                                                      <Text
                                                        as="span"
                                                        key={childId}
                                                        fontStyle="italic"
                                                      >
                                                        {text}
                                                      </Text>
                                                    );
                                                  return formattedText;
                                                }
                                                return null;
                                              })}
                                            </Heading>
                                          );

                                        case 'list': {
                                          const ListComponent =
                                            format === 'ordered' ? 'ol' : 'ul';
                                          return (
                                            <Box
                                              as={ListComponent}
                                              key={blockId}
                                              pl={4}
                                              mb={2}
                                            >
                                              {children?.map((listItem) => {
                                                const {
                                                  children: itemChildren,
                                                } = listItem;
                                                const itemId = `${blockId}-item-${Math.random().toString(36).substr(2, 9)}`;

                                                return (
                                                  <Text
                                                    as="li"
                                                    key={itemId}
                                                    mb={1}
                                                  >
                                                    {itemChildren?.map(
                                                      (child) => {
                                                        const {
                                                          type: childType,
                                                          text,
                                                          bold,
                                                          italic,
                                                        } = child;
                                                        const childId = `${itemId}-${childType}-${Math.random().toString(36).substr(2, 9)}`;

                                                        if (
                                                          childType === 'text'
                                                        ) {
                                                          let formattedText =
                                                            text;
                                                          if (bold)
                                                            formattedText = (
                                                              <Text
                                                                as="span"
                                                                key={childId}
                                                                fontWeight="bold"
                                                                color={
                                                                  cilColors.teal
                                                                }
                                                              >
                                                                {text}
                                                              </Text>
                                                            );
                                                          if (italic)
                                                            formattedText = (
                                                              <Text
                                                                as="span"
                                                                key={childId}
                                                                fontStyle="italic"
                                                              >
                                                                {text}
                                                              </Text>
                                                            );
                                                          return formattedText;
                                                        }
                                                        return null;
                                                      }
                                                    )}
                                                  </Text>
                                                );
                                              })}
                                            </Box>
                                          );
                                        }

                                        case 'quote':
                                          return (
                                            <Box
                                              key={blockId}
                                              borderLeft="4px solid"
                                              borderColor={cilColors.teal}
                                              pl={4}
                                              py={2}
                                              bg="gray.50"
                                              borderRadius="md"
                                              mb={2}
                                            >
                                              {children?.map((child) => {
                                                const {
                                                  type: childType,
                                                  text,
                                                  bold,
                                                  italic,
                                                } = child;
                                                const childId = `${blockId}-${childType}-${Math.random().toString(36).substr(2, 9)}`;

                                                if (childType === 'text') {
                                                  let formattedText = text;
                                                  if (bold)
                                                    formattedText = (
                                                      <Text
                                                        as="span"
                                                        key={childId}
                                                        fontWeight="bold"
                                                      >
                                                        {text}
                                                      </Text>
                                                    );
                                                  if (italic)
                                                    formattedText = (
                                                      <Text
                                                        as="span"
                                                        key={childId}
                                                        fontStyle="italic"
                                                      >
                                                        {text}
                                                      </Text>
                                                    );
                                                  return formattedText;
                                                }
                                                return null;
                                              })}
                                            </Box>
                                          );

                                        default:
                                          return (
                                            <Text
                                              key={blockId}
                                              mb={2}
                                              lineHeight="1.6"
                                              textAlign="center"
                                            >
                                              {children?.map((child) => {
                                                const {
                                                  type: childType,
                                                  text,
                                                } = child;
                                                if (childType === 'text')
                                                  return text;
                                                return null;
                                              })}
                                            </Text>
                                          );
                                      }
                                    })}
                                  </Box>
                                );
                              }

                              // Fallback per altri formati
                              return (
                                <Text
                                  color="gray.500"
                                  fontStyle="italic"
                                  textAlign="center"
                                >
                                  Formato descrizione non supportato
                                </Text>
                              );
                            })()}
                          </Box>

                          {/* Vantaggi esclusivi migliorati */}
                          {tier.sconti && tier.sconti.length > 0 && (
                            <Box mb={6} flex="1">
                              <Box
                                bg="linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)"
                                borderRadius="xl"
                                p={4}
                                border="1px solid"
                                borderColor="gray.200"
                              >
                                <Text
                                  fontSize="sm"
                                  align="center"
                                  fontWeight="bold"
                                  color={cilColors.pink}
                                  mb={4}
                                  textTransform="uppercase"
                                  letterSpacing="wide"
                                >
                                  🎁 Vantaggi Esclusivi
                                </Text>
                                <VStack spacing={3} align="center">
                                  {tier.sconti
                                    .slice(0, 3)
                                    .map((sconto, scontoIndex) => {
                                      const TypeIcon = getTypeIcon(sconto.type);
                                      const typeColor = getTypeColor(
                                        sconto.type
                                      );

                                      return (
                                        <Button
                                          key={`${tier.documentId}-sconto-${sconto.documentId || sconto.name || scontoIndex}`}
                                          variant="ghost"
                                          size="sm"
                                          onClick={() =>
                                            openVantaggiModal(sconto)
                                          }
                                          _hover={{
                                            bg: `${typeColor}.50`,
                                            color: `${typeColor}.700`,
                                            transform: 'translateX(6px)',
                                            boxShadow:
                                              '0 4px 12px rgba(0, 0, 0, 0.1)',
                                            borderColor: `${typeColor}.200`,
                                          }}
                                          _active={{ transform: 'scale(0.95)' }}
                                          transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                                          leftIcon={
                                            <Icon
                                              as={TypeIcon}
                                              color={`${typeColor}.500`}
                                            />
                                          }
                                          rightIcon={
                                            sconto.sponsors &&
                                            sconto.sponsors.length > 0 ? (
                                              <Badge
                                                colorScheme={typeColor}
                                                variant="solid"
                                                fontSize="xs"
                                                borderRadius="md"
                                              >
                                                {sconto.sponsors.length}
                                              </Badge>
                                            ) : null
                                          }
                                          justifyContent="space-between"
                                          w="full"
                                          maxW="300px"
                                          h="auto"
                                          py={3}
                                          px={4}
                                          borderRadius="md"
                                          border="1px solid"
                                          borderColor="gray.200"
                                          bg="white"
                                        >
                                          <Box textAlign="left" flex="1">
                                            <Text
                                              fontWeight="semibold"
                                              fontSize="sm"
                                              color="gray.700"
                                            >
                                              {sconto.name}
                                            </Text>
                                            {sconto.percentageAmount && (
                                              <Text
                                                fontSize="xs"
                                                color={`${typeColor}.600`}
                                                fontWeight="medium"
                                              >
                                                {sconto.percentageAmount}% di
                                                sconto
                                              </Text>
                                            )}
                                          </Box>
                                        </Button>
                                      );
                                    })}
                                  {tier.sconti.length > 3 && (
                                    <Text
                                      fontSize="xs"
                                      color="gray.500"
                                      fontStyle="italic"
                                    >
                                      +{tier.sconti.length - 3} altri vantaggi
                                    </Text>
                                  )}
                                </VStack>
                              </Box>
                            </Box>
                          )}

                          {/* Vantaggi dinamici dal campo data */}
                          {tier.data?.benefits && (
                            <Box mb={6} flex="1">
                              <Text
                                fontSize="sm"
                                fontWeight="semibold"
                                color={cilColors.teal}
                                mb={3}
                              >
                                🎁 Vantaggi inclusi:
                              </Text>
                              {tier.data.benefits.map((benefit) => (
                                <Flex
                                  key={`${tier.documentId}-benefit-${benefit.merchant}-${benefit.description}`}
                                  align="center"
                                  mb={2}
                                >
                                  <Text
                                    fontSize="sm"
                                    color={cilColors.teal}
                                    fontWeight="bold"
                                    minW="30px"
                                  >
                                    {benefit.icon}
                                  </Text>
                                  <Text fontSize="sm" color="gray.700" flex="1">
                                    <strong>{benefit.merchant}</strong> -{' '}
                                    {benefit.description}
                                  </Text>
                                  <Badge
                                    colorScheme="teal"
                                    variant="solid"
                                    fontSize="xs"
                                    px={2}
                                    py={1}
                                  >
                                    {benefit.discount}
                                  </Badge>
                                </Flex>
                              ))}
                            </Box>
                          )}

                          {/* Bottone di abbonamento migliorato */}
                          <Button
                            bg={cilColors.teal}
                            color="white"
                            _hover={{
                              bg: '#359f88',
                              transform: 'scale(1.05) translateY(-2px)',
                              boxShadow: '0 10px 30px rgba(62, 178, 151, 0.4)',
                            }}
                            w="full"
                            size="lg"
                            onClick={() => {
                              handleSubscribe(tier);
                              track(`cta_abbonati_tier_${tier.documentId}`);
                            }}
                            mt="auto"
                            transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                            fontWeight="bold"
                            fontSize="md"
                            py={6}
                            borderRadius="md"
                            boxShadow="0 4px 20px rgba(62, 178, 151, 0.3)"
                            _active={{
                              transform: 'scale(0.98)',
                              boxShadow: '0 2px 10px rgba(62, 178, 151, 0.5)',
                            }}
                          >
                            Abbonati Ora
                          </Button>
                        </Box>
                      </Box>
                    );
                  })}
              </SimpleGrid>
            )}

            {/* Bottone "Come funziona" migliorato */}
            <Box textAlign="center" mt={8}>
              <Button
                size="xl"
                bg="rgba(255, 255, 255, 0.15)"
                color="white"
                _hover={{
                  bg: 'rgba(255, 255, 255, 0.25)',
                  transform: 'scale(1.05) translateY(-2px)',
                  boxShadow: '0 10px 30px rgba(255, 255, 255, 0.2)',
                }}
                px={10}
                py={6}
                fontWeight="bold"
                fontSize="lg"
                transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
                onClick={() => {
                  setIsHowItWorksOpen(true);
                  track('cta_come_funziona');
                }}
                borderRadius="md"
                backdropFilter="blur(10px)"
                border="2px solid rgba(255, 255, 255, 0.2)"
                leftIcon={<FaCalculator />}
                _active={{
                  transform: 'scale(0.98)',
                  boxShadow: '0 5px 15px rgba(255, 255, 255, 0.3)',
                }}
              >
                Come funziona?
              </Button>
            </Box>
          </VStack>
        </Container>
      </Box>

      {/* CTA FINALE */}
      <Box py={16} bg={cilColors.plum} color="white">
        <Container maxW="800px" textAlign="center">
          <VStack spacing={8}>
            <Box
              bg="linear-gradient(135deg, #ffcc00 0%, #e6b800 100%)"
              borderRadius="lg"
              p={4}
              boxShadow="0 8px 32px rgba(255, 204, 0, 0.3)"
              position="relative"
              _before={{
                content: '""',
                position: 'absolute',
                top: '-2px',
                left: '-2px',
                right: '-2px',
                bottom: '-2px',
                borderRadius: 'lg',
                background:
                  'linear-gradient(45deg, #ffcc00, #3eb297, #36a9e1, #e95995)',
                zIndex: -1,
                animation: 'spin 4s linear infinite',
              }}
              sx={{
                '@keyframes spin': {
                  '0%': { transform: 'rotate(0deg)' },
                  '100%': { transform: 'rotate(360deg)' },
                },
              }}
            >
              <Icon as={FaUsers} w={8} h={8} color={cilColors.plum} />
            </Box>

            <Heading size="2xl">Diventa Sostenitore Mensile</Heading>

            <Text fontSize="lg" maxW="600px" lineHeight="1.7">
              Con il tuo abbonamento mensile sostieni CIL in modo continuativo e
              accedi a vantaggi esclusivi riservati ai nostri sostenitori.
            </Text>

            <Button
              size="xl"
              bg={cilColors.yellow}
              color={cilColors.plum}
              _hover={{ bg: '#e6b800', transform: 'scale(1.05)' }}
              onClick={() => {
                document
                  .getElementById('subscription-plans')
                  ?.scrollIntoView({ behavior: 'smooth' });
                track('cta_finale_abbonati');
              }}
              px={12}
              py={6}
              fontWeight="bold"
              fontSize="lg"
              leftIcon={<FaUsers />}
              transition="all 0.3s"
              borderRadius="md"
            >
              Scegli il Tuo Piano
            </Button>
          </VStack>
        </Container>
      </Box>

      {/* Subscription Drawer */}
      {selectedTier && (
        <SubscriptionDrawer
          isOpen
          onClose={() => setSelectedTier(null)}
          tier={selectedTier}
          clientSecret={subClientSecret}
          localSubscriptionId={subLocalSubscriptionId}
        />
      )}

      {/* How It Works Modal */}
      <HowItWorksModal
        isOpen={isHowItWorksOpen}
        onClose={() => setIsHowItWorksOpen(false)}
      />

      {/* Vantaggi Esclusivi Modal */}
      <VantaggiEsclusiviModal
        isOpen={isVantaggiModalOpen}
        onClose={() => {
          setIsVantaggiModalOpen(false);
          setSelectedSconto(null);
        }}
        sconto={selectedSconto}
      />
    </Box>
  );
};

export default Patreon;
